import {
  CertificationLevel,
  CommercialModel,
  ApplicationCategory,
  BenefitIconType,
  ApplicationType,
  NoCodeAppType,
  ApplicationMediaSubcategory,
  ApplicationProductionSubcategory,
} from 'types/applications/enums'
import { RegionMarketsDTO } from 'types/applications/market'
import { AttachmentMeta, ImageMetaDTO, LogoMetaDTO, VideoMetaDTO } from 'types/common/attachments'
import { RichTextDTO } from 'types/common/fields'
import { MayBeNull } from 'types/common/utils'
import { UserDTO } from 'types/users/user'

export interface BaseApplicationDTO {
  id: string
  name: string
  applicationType: ApplicationType
  noCodeAppType: MayBeNull<NoCodeAppType>
  logoMeta: LogoMetaDTO
  shortDescription: string
  prerequisites: MayBeNull<RichTextDTO>
  fullDescription: MayBeNull<RichTextDTO>
  benefits: MayBeNull<Benefit[]>
  applicationCategory: ApplicationCategoryDTO
  highlights: MayBeNull<Highlight[]>
  commercialModel: MayBeNull<{
    model?: CommercialModel
    description?: RichTextDTO
  }>
  geographies: MayBeNull<RegionMarketsDTO[]>
  isGlobalRegionSet: boolean
  ownerName: string
  ownerEmail: string
  supportContact: MayBeNull<string>
  supportEmail: MayBeNull<string>

  relatedProductIds: string[]

  //MP custom
  agencyUuid: string
  tenantId: string
  attachmentMetas: MayBeNull<ImageMetaDTO[]>
  videoMetas: MayBeNull<VideoMetaDTO[]>
  isAvailable: boolean //TODO check
}

// TODO rename to NativeAppDTO
export interface ApplicationDTO extends BaseApplicationDTO {
  productTour: MayBeNull<NamedVideoMetaDTO>
  trainings: MayBeNull<NamedVideoMetaDTO[]>

  marketingMaterials: MayBeNull<AttachmentMeta[]>
  pricingModel: MayBeNull<CommercialModel>
  reviewCount: MayBeNull<number>
  overallReviewScore: MayBeNull<number>

  isAvailable: boolean

  usedFor: MayBeNull<string[]>
  details: MayBeNull<string[]>
  features: MayBeNull<Feature[]>
  commercials: MayBeNull<RichTextDTO>
  glossary: MayBeNull<{
    description?: RichTextDTO
    attachment?: AttachmentMeta[]
  }>
  certification: MayBeNull<CertificationLevel>

  developmentRoadMap: MayBeNull<AttachmentMeta>
  knowledgeBase: MayBeNull<{
    url: string
    attachment?: AttachmentMeta[]
  }>
  pricing: MayBeNull<AttachmentMeta>
  infographic: MayBeNull<AttachmentMeta>
  pitchDeck: MayBeNull<AttachmentMeta>
  caseStudies: MayBeNull<AttachmentMeta[]>
}

export interface NoCodeAppDTO extends BaseApplicationDTO {}

export type CombinedApplicationDTO = ApplicationDTO | NoCodeAppDTO

export interface Benefit {
  icon: BenefitIconType
  title: string
  description: string
  ordering: number
}

export interface Feature {
  name: string
  attachmentMeta: MayBeNull<AttachmentMeta>
  description: MayBeNull<RichTextDTO>
}

export type Highlight = {
  headline: string
  highlight: string
}

export type NamedVideoMetaDTO = {
  name: string
  url: string
  description?: string
}

export type ApplicationReviewScore = {
  overallScore: number
  reviewCount: number
  currentUserReviewId?: ApplicationReviewDTO['id']
} & ApplicationPillsFormDTO

export type ApplicationReviewDTO = {
  id: string
  applicationId: string
  createdAt: string
  updatedAt: string
  user: UserDTO
  userTenantName: string
  reviewComment: string
  reviewVote: number
  voteCount: number
  helpfulVoteCount: number
  unhelpfulVoteCount: number
  currentUserVoted: boolean
  currentUserFindsHelpful: boolean
} & ApplicationPillsFormDTO

export type ApplicationReviewFormDTO = {
  reviewComment: string
  reviewVote: number
} & ApplicationPillsFormDTO

export type ApplicationPillsFormDTO = {
  usedFor: MayBeNull<string[]>
  details: MayBeNull<string[]>
}

export type ApplicationSubcategoryType = ApplicationMediaSubcategory | ApplicationProductionSubcategory

export interface ApplicationCategoryDTO {
  category: ApplicationCategory
  subcategory?: ApplicationSubcategoryType
}

export enum CategoryCode {
  CATEGORIES = 'categories',
  MARKETS = 'markets',
  REGIONS = 'regions',
  COMMERCIAL_MODELS = 'commercialModels',
  IS_AVAILABLE = 'isAvailable',
  GLOBAL_REGION = 'globalRegion',
  TENANT_IDS = 'tenantIds',
  PRODUCT_IDS = 'productIds',
}
